import React, { FC, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import { WidgetSettings } from "./models/WidgetSettings";
import { WidgetCredentials } from "./models/WidgetSettings";
import { headers } from "./services/utils";
import {getAreaId, fetchWidgetSettings, isURLPage, isURLDemo, getCompanyLink} from "./utils/url";
import Alert from "@mui/material/Alert";
import {helsingborgPropertiesIds} from "./constants/constants";
import {COMPANY_LINKS} from "./constants/companyLinks";
import Providers from "./providers";
import {findWidgetRoot, WIDGET_ROOT_ID} from "./utils/widgetRoot";
import {authorization, isTokenRejected, takeAuthTokenFromURL} from "./utils/authToken";

type GlobalsProps = {
  // the element with the widget attributes, see utils/widgetRoot
  credentials: HTMLElement;
  companyIdAPIKey: string | null;
}

const Globals: FC<GlobalsProps> = ({credentials, companyIdAPIKey}) => {
  const [widgetSettings, setWidgetSettings] = useState<WidgetSettings[] | undefined>(undefined);
  const [receivedSettings, setReceivedSettings] = useState<boolean>(false);
  const [tokenRejected, setTokenRejected] = useState<boolean>(false);
  const [widgetCredentials, setWidgetCredentials] = useState<WidgetCredentials>({
    buildingAreaID: credentials.getAttribute("buildingAreaId"),
    propertyID: credentials.getAttribute("propertyId")
  });
  const [isOpenDefault, setIsOpenDefault] = useState<boolean>(false);
  const [companyID, setCompanyID] = useState<string | null>(companyIdAPIKey);
  const {buildingAreaId, propertyId} = getAreaId(widgetCredentials);

  useEffect(() => {
    if (isURLPage()) {
      setIsOpenDefault(true);
    }

    const url = new URL(window.location.href);
    const id = url.pathname.split("/").pop();
    const helsingborgId = helsingborgPropertiesIds.find((i) => i === id)
    if (helsingborgId) {
      const helsingborgApiKey = COMPANY_LINKS['helsingborg-lekplatser'].apiKey
      setCompanyID(helsingborgApiKey)
      setWidgetCredentials({
        buildingAreaID: '',
        propertyID: helsingborgId
      })
      headers.Authorization = authorization(helsingborgApiKey);
      setIsOpenDefault(true)
    }

    const companyLink = getCompanyLink()
    if (companyLink) {
      setCompanyID(companyLink.apiKey)
      setWidgetCredentials({
        buildingAreaID: companyLink.buildingAreaId ?? '',
        propertyID: companyLink.propertyId ?? ''
      })
      headers.Authorization = authorization(companyLink.apiKey);
      setIsOpenDefault(true);
    }
  }, [])

  useEffect(() => {
    if (companyID) {
      (async () => {
        try {
          const settings = await fetchWidgetSettings(buildingAreaId, propertyId);
          if (settings) {
            setWidgetSettings(settings);
          }
          setReceivedSettings(true);
        } catch (error) {
          if (isTokenRejected(error)) {
            setTokenRejected(true);
          } else {
            console.error(error);
          }
        }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyID])

  return (
    <Providers>
      {tokenRejected ?
          <Alert severity="error"><strong>Error! The link has expired, open the widget from the AMS again.</strong></Alert>
          : receivedSettings && (buildingAreaId || propertyId) && companyID ?
          <App
              openDefault={isOpenDefault}
              companyIdAPIKey={companyID}
              widgetAccessibilityButtonSettings={widgetSettings}
              buildingAreaId={buildingAreaId}
              propertyId={propertyId}
          />
          : (!buildingAreaId && !propertyId) ?
              <Alert severity="error"><strong>Error! Add correct link.</strong></Alert> : null
      }
    </Providers>
  );
}

const mount = () => {
  const root = findWidgetRoot(document);
  if (!root) {
    console.error(`[Accessibility widget] The page has no element with the id "${WIDGET_ROOT_ID}" to show the widget in.`);
    console.error("[Accessibility widget] Something went wrong when inserting the widget, contact our support if you need help.");
    return;
  }
  // the widget pages can be opened from the AMS with the token of its user, not the sites the widget is embedded in
  if (isURLPage() || isURLDemo()) {
    takeAuthTokenFromURL();
  }
  headers.Authorization = authorization(root.apiKey);
  ReactDOM.render(
    <Globals credentials={root.credentials} companyIdAPIKey={root.apiKey}/>,
    root.container
  );
}

// the script can be loaded before the element it renders in is parsed
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
